<template>
  <RootErrorBoundary>
    <Modal.Container />
    <VettingModal v-if="shouldShowVettingModal" />

    <NotificationContainer />
    <Loader />

    <AppErrorBoundary>
      <div
        id="main-container"
        :class="{impersonation: isImpersonated}">
        <ImpersonationBanner v-if="isImpersonated" />

        <HeaderWrapper />

        <div id="main-panels">
          <SideMenuContainer />

          <div
            ref="scrollContainerRef"
            class="flex flex-1 flex-col overflow-y-scroll"
            data-test="page-ready">
            <AppMessages />

            <Suspense>
              <RouterView class="grow" />
            </Suspense>
          </div>
        </div>
      </div>
    </AppErrorBoundary>

    <Suspense>
      <WebSocketProvider />
    </Suspense>

    <ExternalServiceProvider />
  </RootErrorBoundary>
</template>

<script setup lang="ts">
import {NotificationContainer} from '@myparcel-frontend/notifications';
import {useTitle} from '@vueuse/core';
import {provide, ref, computed, useTemplateRef, onMounted} from 'vue';
import {useI18n} from 'vue-i18n';
import {RouterView, useRoute} from 'vue-router';
import VettingModal from './components/modals/VettingModal/VettingModal.vue';
import useAuth from './composables/useAuth';
import useCarriers from './composables/useCarriers/useCarriers';
import {useServiceAccount} from './composables/useServiceAccount/useServiceAccount';
import {useVettingCheck} from './composables/useVettingCheck/useVettingCheck';
import WebSocketProvider from './providers/WebSocketProvider.vue';
import AppErrorBoundary from '@/components/ui/AppErrorBoundary/AppErrorBoundary.vue';
import AppMessages from '@/components/ui/AppMessages/AppMessages.vue';
import ImpersonationBanner from '@/components/ui/ImpersonationBanner/ImpersonationBanner.vue';
import Loader from '@/components/ui/Loader';
import Modal from '@/components/ui/Modal';
import RootErrorBoundary from '@/components/ui/RootErrorBoundary/RootErrorBoundary.vue';
import HeaderWrapper from '@/components/ui/layout/header/HeaderWrapper.vue';
import SideMenuContainer from '@/components/ui/layout/sideMenu/SideMenuContainer.vue';
import ExternalServiceProvider from '@/providers/ExternalServiceProvider.vue';
import {scrollContainerKey} from '@/utils/symbols';

const {getAuth0ImpersonatedAccountId} = useAuth();
const {isImpersonated: isImpersonatedFromIamService} = useServiceAccount();

const {t} = useI18n();
const route = useRoute();
const {getAccountCarriers} = useCarriers();
const {shouldShowVettingModal} = useVettingCheck();

const title = computed(() => {
  return route.meta.title ? `MyParcel - ${route.meta.title(t)}` : 'MyParcel';
});

useTitle(title);

const scrollContainerRef = useTemplateRef('scrollContainerRef');
const isImpersonatedFromAuth0 = ref(!!getAuth0ImpersonatedAccountId());

const isImpersonated = computed(() => isImpersonatedFromAuth0.value || isImpersonatedFromIamService.value);

/*
 * Provide the scroll container ref.
 * This is where all content is rendered.
 * This is could be used to alter the scroll position.
 */
provide(scrollContainerKey, scrollContainerRef);

onMounted(() => {
  void getAccountCarriers();
});
</script>

<style lang="scss" scoped>
@reference "./assets/css/index.css";

#main-wrapper {
  @apply grow flex flex-col;
}

#main-container {
  @apply flex flex-col;
}

#main-panels {
  @apply flex h-[calc(100dvh-70px)];
}

.impersonation {
  & #main-panels {
    @apply flex h-[calc(100dvh-94px)];
  }
}
</style>
